// src/lib/privacy/deadlineReminders.ts

import { getAllPrivacyRequests } from './privacyRequestApi';
import { PrivacyRequest } from '../../types/privacyRequest';
import { sendPrivacyRequestDeadlineReminder } from '../email/emailService';

/**
 * Checks for privacy requests approaching deadlines and sends reminders
 * Should be called daily (e.g., via scheduled function or cron job)
 */
export async function checkAndSendDeadlineReminders(): Promise<void> {
  try {
    const allRequests = await getAllPrivacyRequests();
    
    // Filter requests that are not completed and have deadlines
    const activeRequests = allRequests.filter(
      (req) => req.status !== 'completed' && 
               req.status !== 'rejected' && 
               req.deadline
    );
    
    // Check each request for approaching deadlines
    const remindersToSend: Array<{
      request: PrivacyRequest;
      daysRemaining: number;
      deadlineDate: string;
    }> = [];
    
    const now = new Date();
    
    for (const request of activeRequests) {
      try {
        const deadlineDate = request.deadline.toDate 
          ? request.deadline.toDate() 
          : new Date(request.deadline);
        
        const diffTime = deadlineDate.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        // Send reminders at 30, 14, 7, 3, and 1 days before deadline
        // Also check if we've already sent a reminder today for this request
        // (In production, you'd want to track when reminders were sent)
        if (diffDays === 30 || diffDays === 14 || diffDays === 7 || diffDays === 3 || diffDays === 1) {
          remindersToSend.push({
            request,
            daysRemaining: diffDays,
            deadlineDate: deadlineDate.toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })
          });
        }
      } catch (error) {
        console.error(`Error processing deadline for request ${request.id}:`, error);
      }
    }
    
    // Send reminder emails
    for (const { request, daysRemaining, deadlineDate } of remindersToSend) {
      try {
        await sendPrivacyRequestDeadlineReminder(
          request.requestType,
          request.email,
          request.id || '',
          daysRemaining,
          deadlineDate
        );
        console.log(`Sent deadline reminder for request ${request.id} (${daysRemaining} days remaining)`);
      } catch (error) {
        console.error(`Failed to send deadline reminder for request ${request.id}:`, error);
      }
    }
    
    console.log(`Checked ${activeRequests.length} active requests, sent ${remindersToSend.length} reminders`);
  } catch (error) {
    console.error('Error checking deadline reminders:', error);
    throw error;
  }
}

/**
 * Gets requests that are approaching deadlines (for admin dashboard display)
 */
export function getRequestsApproachingDeadline(requests: PrivacyRequest[]): PrivacyRequest[] {
  const now = new Date();
  const approachingThreshold = 7; // Days
  
  return requests.filter((request) => {
    if (request.status === 'completed' || request.status === 'rejected' || !request.deadline) {
      return false;
    }
    
    try {
      const deadlineDate = request.deadline.toDate 
        ? request.deadline.toDate() 
        : new Date(request.deadline);
      
      const diffTime = deadlineDate.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      return diffDays >= 0 && diffDays <= approachingThreshold;
    } catch {
      return false;
    }
  });
}
